import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { PlainInput } from 'components/PlainInput'
import { defaultLoanServicingStatusKeys } from 'config'
import { useEffect, useState } from 'react'
import { getAdminConfig, setAdminConfig } from 'services'
import { Checkbox } from 'stories/components/Checkbox/Checkbox'
import { confirm, generateUniqeId, prompt } from 'utils'

interface ILoanServicingStatusConfig {
  title: string
  visible: boolean
}

export const LoanServicingStatus = () => {
  const [isLoading, setLoading] = useState(false)
  const [data, setData] = useState<Record<string, ILoanServicingStatusConfig>>({})

  useEffect(() => {
    setLoading(true)
    getAdminConfig('loanServicingStatus')
      .then(setData)
      .finally(() => setLoading(false))
  }, [])

  const onSync = async (_data?: any) => {
    setLoading(true)

    setAdminConfig('loanServicingStatus', _data || data)
      .then(() => {})
      .finally(() => setLoading(false))
  }

  const onChange = (key: string, value: any, map: string) => {
    let temp = cloneDeep(data) as any
    if (temp[key] === undefined) temp[key] = {}
    temp[key][map] = value
    setData(temp)
    onSync(temp)
  }

  const onAdd = async () => {
    const title = await prompt('Please enter the new title')
    if (!title) return
    const newData = cloneDeep(data)
    const newKey = generateUniqeId(title, Object.keys(data))
    if (!newKey) return
    newData[newKey] = {
      title,
      visible: true,
    }
    setData(newData)
    onSync(newData)
  }

  const onRemove = async (key: string) => {
    const result = await confirm('Are you sure you want to remove this item?')
    if (!result) return
    const newData = cloneDeep(data)
    delete newData[key]
    setData(newData)
    onSync(newData)
  }

  return (
    <div className="relative">
      <LayoutLoading show={isLoading} />
      <div className={`border-b-2 border-shade-blue w-full italic flex justify-between items-center`}>
        <p>- Servicing Pipeline</p>
        <div className="flex">
          <span className="btn-icon" onClick={onAdd}>
            <PlusIcon className="w-4 h-4" />
          </span>
        </div>
      </div>
      <div className="mt-4">
        <table className="text-sm">
          <thead className="uppercase text-xs bg-gray-50 dark:bg-gray-700">
            <tr>
              {['No', 'Title', 'In Use', 'Action'].map((v) => (
                <th scope="col" className="p-3 border" key={`header-${v}`}>
                  {v}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.keys(data).map((key, index) => {
              const { title, visible } = data[key]
              const isCustom = !defaultLoanServicingStatusKeys.includes(key)
              return (
                <tr key={index}>
                  <td className="p-2 border">{index + 1}</td>
                  <td className="px-3 border">
                    <PlainInput
                      value={title}
                      content={title}
                      onChange={(value: string) => onChange(key, value, 'title')}
                    />
                  </td>
                  <td className="px-3 border text-center">
                    <Checkbox
                      color="gray"
                      id={key}
                      title={''}
                      key={key}
                      value={visible}
                      onChange={(value) => onChange(key, value, 'visible')}
                      className="w-[none]"
                    />
                  </td>
                  <td className="px-3 border">
                    {isCustom && (
                      <div className="flex">
                        <span className="btn-icon" onClick={() => onRemove(key)}>
                          <TrashIcon className="w-4 h-4 text-red-500" />
                        </span>
                      </div>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
